import {
  Autocomplete,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { isFulfilled } from "@reduxjs/toolkit";
import { format, isValid } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import {
  Controller,
  FieldValues,
  FormProvider,
  useForm,
  ValidateResult,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import DialogContentWrapper from "../../../../common/components/functional/dialogs/DialogContentWrapper";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import isEmpty from "../../../../common/helpers/isEmpty";
import {
  buildZonedSalaryComponents,
  formatSalaryComponents,
} from "../../../../common/utils/formatSalaryComponents";
import { UnsavedChangesProvider } from "../../../../contexts/UnsavedChangesContext";
import useTesAutocompleteQuery from "../../../../hooks/useTesAutocompleteQuery";
import {
  applyTesSalaryTableCell,
  fetchEmploymentAttributesForEditing,
  SetEmpAttrSalaryReducerParams,
  setNewEmpAttrSalary,
} from "../../../employment/employmentSlice";
import { Talent } from "../../../talents/talentAPI";
import { TesSalaryTableSelectionProvenance } from "../../../tesSalaryTables/tesSalaryTablesApi";
import { EditMode } from "../../TalentDetails";
import SalaryComponents from "./SalaryComponents";
import {
  getAdditionalSalaryComponentsForTesTableApply,
  withFullSalaryMode,
  withInfoOnlyMode,
} from "./tesSalaryTableFormUtils";

type Props = {
  talent: Talent | null;
  onClose: () => void;
  editMode: EditMode;
};

const validateDate = (date: Date | null): ValidateResult => {
  if (!date || !isValid(date)) return false;

  const formattedDate = format(date, "yyyy-MM-dd");
  const dateParts = formattedDate.split("-");
  const yearContainsLeadingZeros = /^0+/.test(dateParts?.[0]);

  return dateParts && formattedDate.length === 10 && !yearContainsLeadingZeros;
};

type TalentShiftSalariesFromValues = {
  tesId: string | null;
  validFrom: Date | null;
  workTitleId: string | null;
  components: { [k: string]: string };
  totalSalary: string;
  tesSalaryTableSelection: TesSalaryTableSelectionProvenance | null;
};

const TalentShiftSalariesForm = ({ talent, onClose, editMode }: Props) => {
  const { t } = useTranslation();
  const { tes, workTitle } = useAppSelector((state) => state.localized);
  const { setTesSearchTerm, tesIdsQueryResult } = useTesAutocompleteQuery();
  const [selectedTableSelection, setSelectedTableSelection] =
    useState<TesSalaryTableSelectionProvenance | null>(null);
  const [isTesSalaryTableAvailable, setIsTesSalaryTableAvailable] =
    useState(false);

  const employmentAttributesForEditing = useAppSelector(
    (state) => state.employment.attributesForEditing,
  );
  const { status } = useAppSelector((state) => state.employment.attributes);
  const isLoading = status === "pending";

  const dispatch = useAppDispatch();

  const talentWorkTitleOptions =
    talent?.workTitles?.values && !!talent.workTitles.values.length
      ? talent.workTitles.values.map((workTitle) => workTitle.id)
      : [];

  const defaultValues: TalentShiftSalariesFromValues = {
    tesId: null,
    validFrom: new Date(),
    workTitleId: null,
    components: {},
    totalSalary: "",
    tesSalaryTableSelection: null,
  };

  const methods = useForm({
    mode: "onChange",
    defaultValues,
  });

  const workTitleId = methods.watch("workTitleId");
  const tesId = methods.watch("tesId");
  const validFrom = methods.watch("validFrom");
  const salaryZones = employmentAttributesForEditing.attributes?.salaryZones;

  useEffect(() => {
    if (
      talent?.id &&
      workTitleId &&
      tesId &&
      validFrom &&
      validateDate(validFrom)
    ) {
      dispatch(
        fetchEmploymentAttributesForEditing({
          talentId: talent.id,
          workTitleId,
          tesId,
        }),
      );
    }
  }, [workTitleId, tesId, validFrom, talent?.id, dispatch]);

  useEffect(() => {
    setSelectedTableSelection(null);
    setIsTesSalaryTableAvailable(false);
  }, [tesId, workTitleId]);

  const handleAvailabilityChange = useCallback(
    (isAvailable: boolean) => {
      setIsTesSalaryTableAvailable(isAvailable);
      if (!isAvailable) {
        setSelectedTableSelection(null);
        setIsTesSalaryTableAvailable(false);
        if (methods.getValues("tesSalaryTableSelection")) {
          methods.setValue("tesSalaryTableSelection", null);
        }
      }
    },
    [methods],
  );

  const onSave = (data: FieldValues) => {
    if (data.validFrom && data.workTitleId && talent?.id && data.tesId) {
      // TODO: refactor date formatting
      const date = format(data.validFrom, "yyyy-MM-dd");

      const formattedSalary = formatSalaryComponents(data.components);
      const formattedZonedSalary = buildZonedSalaryComponents(
        data.zonedComponents,
      );
      const hasZonedComponents = !!formattedZonedSalary;

      if (employmentAttributesForEditing.attributes) {
        const canApplySelectedTableCellDirectly =
          isTesSalaryTableAvailable &&
          !!selectedTableSelection &&
          !hasZonedComponents;

        const additionalSalary =
          getAdditionalSalaryComponentsForTesTableApply(formattedSalary);

        // console.log("XXX additionalSalary: ", additionalSalary)
        if (canApplySelectedTableCellDirectly) {
          dispatch(
            applyTesSalaryTableCell({
              talentId: talent.id,
              version: employmentAttributesForEditing.attributes.version,
              workTitleId: data.workTitleId,
              tesId: data.tesId,
              from: date,
              tesSalaryTableId: selectedTableSelection.tesSalaryTableId,
              rowId: selectedTableSelection.rowId,
              columnId: selectedTableSelection.columnId,
              mode: selectedTableSelection.mode,
              salary:
                selectedTableSelection.mode === "INFO_ONLY"
                  ? formattedSalary
                  : additionalSalary,
              totalSalary: data?.totalSalary,
            }),
          ).then((action) => {
            if (isFulfilled(action)) {
              onClose();
            }
          });
          return;
        }

        const formattedData: SetEmpAttrSalaryReducerParams = {
          salary: formattedSalary,
          talentId: talent?.id,
          version: employmentAttributesForEditing?.attributes.version,
          workTitleId: data.workTitleId,
          tesId: data.tesId,
          from: date,
          totalSalary: data?.totalSalary,
          ...(hasZonedComponents && {
            zonedSalary: formattedZonedSalary,
            zonedSalaryTotal: data.zonedTotalSalary,
          }),
        };

        dispatch(setNewEmpAttrSalary(formattedData)).then((action) => {
          if (isFulfilled(action)) {
            onClose();
          }
        });
      }
    }
  };

  const onCancel = () => onClose();

  const salaryComponents = methods.watch("components");

  const saveDisabled =
    isLoading ||
    !methods.formState.isDirty ||
    !isEmpty(methods.formState.errors) ||
    !methods.formState.isValid ||
    !salaryComponents;

  return (
    <DialogContentWrapper
      title={t("talentProfile:salarySettings")}
      margin
      onSave={methods.handleSubmit(onSave)}
      onCancel={onCancel}
      saveDisabled={saveDisabled}
    >
      <FormProvider {...methods}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <OverlaySpinner isLoading={isLoading} />
          <Grid item textAlign="end" alignSelf="center" xs={4}>
            <Typography color="textSecondary">
              {t("common:workTitle")}
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <Controller
              name="workTitleId"
              control={methods.control}
              render={({ field: { value, onChange } }) => (
                <Autocomplete
                  sx={{ backgroundColor: "rgba(249, 249, 250, 0.6)" }}
                  value={value}
                  options={
                    value && !talentWorkTitleOptions.includes(value)
                      ? [value, ...talentWorkTitleOptions]
                      : talentWorkTitleOptions
                  }
                  isOptionEqualToValue={(option, selected) =>
                    option === selected
                  }
                  getOptionLabel={(workTitleId) =>
                    workTitle[workTitleId] || workTitleId
                  }
                  onChange={(_, newWorkTitleId) => {
                    onChange(newWorkTitleId);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} size="small" name="workTitle" />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={4} alignSelf="center" textAlign="end">
            <Typography variant="secondary">{t("common:tes")}</Typography>
          </Grid>
          <Grid item xs={8} alignSelf="center">
            <Controller
              name="tesId"
              control={methods.control}
              render={({ field: { value, onChange } }) => (
                <Autocomplete
                  sx={{ backgroundColor: "rgba(249, 249, 250, 0.6)" }}
                  value={value}
                  options={
                    value && !tesIdsQueryResult.includes(value)
                      ? [value, ...tesIdsQueryResult]
                      : tesIdsQueryResult
                  }
                  isOptionEqualToValue={(option, selected) =>
                    option === selected
                  }
                  getOptionLabel={(tesId) => tes[tesId] || tesId}
                  onInputChange={(event, newInput) => {
                    if (event?.type === "change") {
                      setTesSearchTerm(newInput);
                    }
                  }}
                  onChange={(_, newTesId) => {
                    onChange(newTesId);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} size="small" name="workTitle" />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ margin: "8px 0" }} />
          </Grid>
          <Grid item xs={4} alignSelf="center" textAlign="end">
            <Typography variant="secondary">
              {t("talentProfile:validFrom")}
            </Typography>
          </Grid>
          <Grid item xs={8} alignSelf="center">
            <Controller
              name="validFrom"
              control={methods.control}
              rules={{ required: true, validate: (date) => validateDate(date) }}
              render={({ field }) => (
                <DesktopDatePicker
                  {...field}
                  value={field.value}
                  format="dd.MM.yyyy"
                  onChange={(newDate: Date | null) => field.onChange(newDate)}
                  slots={{ textField: TextField }}
                  slotProps={{
                    textField: {
                      error: !!methods.formState.errors?.validFrom,
                      size: "small",
                      fullWidth: true,
                      sx: { input: { textAlign: "center" }, width: "135px" },
                    },
                  }}
                />
              )}
            />
          </Grid>
          <SalaryComponents
            tesId={tesId || ""}
            validFrom={validFrom}
            salaryZones={salaryZones}
            hasTesSalaryTableSelection={!!selectedTableSelection}
            onManualBaseComponentChange={() => {
              if (!selectedTableSelection) return;
              const downgraded = withInfoOnlyMode(selectedTableSelection);
              setSelectedTableSelection(downgraded);
              methods.setValue("tesSalaryTableSelection", downgraded, {
                shouldDirty: true,
              });
              methods.trigger("components");
            }}
            tesSalaryTableProps={{
              tesId,
              workTitleId,
              date: validFrom,
              value: selectedTableSelection,
              onAvailabilityChange: handleAvailabilityChange,
              onChange: ({ selection }) => {
                const fullSalarySelection = withFullSalaryMode(selection);
                setSelectedTableSelection(fullSalarySelection);
                methods.setValue(
                  "tesSalaryTableSelection",
                  fullSalarySelection,
                  {
                    shouldDirty: true,
                  },
                );
                methods.trigger("components");
              },
            }}
          />
        </UnsavedChangesProvider>
      </FormProvider>
    </DialogContentWrapper>
  );
};

export default TalentShiftSalariesForm;
